import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const navLinks = [{
  label: 'Platform',
  to: '/platform'
}, {
  label: 'BAS',
  to: '/breach-and-attack-simulation',
  fullLabel: 'Breach and Attack Simulation'
}, {
  label: 'Integrations',
  to: '/integrations'
}, {
  label: 'Research',
  to: '/research'
}, {
  label: 'Company',
  to: '/company/about'
}];
export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'py-3' : 'py-5'}`} data-id="element-287">
      <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-between" data-id="element-288">
        {/* Left Logo */}
        <Link to="/" className="flex-shrink-0 z-10" data-id="element-289">
          <img src="https://www.picussecurity.com/hubfs/light_logo-original-SVG.svg" alt="Picus Security" className="h-7 w-auto" data-id="element-290" />
        </Link>

        {/* Center Pill Nav (Desktop) */}
        <nav className="hidden lg:flex items-center justify-center flex-1 z-10 mx-8" data-id="element-291">
          <div className={`flex items-center p-1 rounded-full border transition-all duration-300 ${scrolled ? 'bg-picus-surface/80 backdrop-blur-md border-picus-border shadow-lg' : 'bg-transparent border-transparent'}`} data-id="element-292">
            {navLinks.map(link => {
            const isActive = location.pathname === link.to || link.to !== '/' && location.pathname.startsWith(link.to);
            return <Link key={link.to} to={link.to} className={`relative px-4 py-2 text-[13px] font-medium transition-colors duration-200 rounded-full whitespace-nowrap ${isActive || hoveredPath === link.to ? 'text-white' : 'text-picus-muted'}`} onMouseEnter={() => setHoveredPath(link.to)} onMouseLeave={() => setHoveredPath(null)} data-id="element-293">
                  <span className="relative z-10" data-id="element-294">{link.label}</span>

                  {hoveredPath === link.to && !isActive && <motion.div layoutId="nav-hover" className="absolute inset-0 bg-white/10 rounded-full z-0" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                type: 'spring',
                bounce: 0.2,
                duration: 0.6
              }} data-id="element-295" />}

                  {isActive && <motion.div layoutId="nav-active" className="absolute inset-0 border border-picus-border bg-picus-surface rounded-full z-0" transition={{
                type: 'spring',
                bounce: 0.2,
                duration: 0.6
              }} data-id="element-296" />}
                </Link>;
          })}
          </div>
        </nav>

        {/* Right CTAs */}
        <div className="hidden lg:flex items-center justify-end gap-2 z-10 flex-shrink-0" data-id="element-297">
          <button className="p-2 text-picus-muted hover:text-white transition-colors rounded-full hover:bg-white/5" data-id="element-298">
            <Search className="w-4 h-4" data-id="element-299" />
          </button>
          <a href="https://app.picussecurity.com" target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-sm font-medium text-picus-muted hover:text-white transition-colors" data-id="element-300">
            Login
          </a>
          <Link to="/free-trial" className="px-4 py-2 text-sm font-medium border border-picus-border text-white rounded-full hover:bg-picus-surface transition-colors" data-id="element-301">
            Free Trial
          </Link>
          <Link to="/contact" className="px-4 py-2 text-sm font-medium bg-white text-black rounded-full hover:bg-gray-200 transition-colors" data-id="element-302">
            Get a Demo
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button className="lg:hidden text-white p-2 z-10 relative" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-303">
          {mobileMenuOpen ? <X data-id="element-304" /> : <Menu data-id="element-305" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-306">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="lg:hidden absolute top-full left-0 right-0 bg-picus-surface border-b border-picus-border shadow-2xl" data-id="element-307">
            <div className="px-6 py-6 flex flex-col gap-4" data-id="element-308">
              {navLinks.map(link => <Link key={link.to} to={link.to} className="text-lg font-medium text-picus-muted hover:text-white transition-colors py-2 border-b border-picus-border/50" data-id="element-309">
                  {link.fullLabel || link.label}
                </Link>)}
              <div className="flex items-center justify-between py-2 border-b border-picus-border/50" data-id="element-310">
                <span className="text-lg font-medium text-picus-muted" data-id="element-311">
                  Search
                </span>
                <Search className="w-5 h-5 text-picus-muted" data-id="element-312" />
              </div>
              <a href="https://app.picussecurity.com" target="_blank" rel="noopener noreferrer" className="text-lg font-medium text-picus-muted hover:text-white transition-colors py-2" data-id="element-313">
                Login
              </a>
              <div className="flex gap-3 mt-4" data-id="element-314">
                <Link to="/free-trial" className="flex-1 px-5 py-3 text-center font-medium border border-picus-border text-white rounded-lg hover:bg-picus-surface transition-colors" data-id="element-315">
                  Free Trial
                </Link>
                <Link to="/contact" className="flex-1 px-5 py-3 text-center font-medium bg-white text-black rounded-lg hover:bg-gray-200 transition-colors" data-id="element-316">
                  Get a Demo
                </Link>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}